import {useCategoryUniqueLearnerStatsQuery} from 'src/generated/graphql';

/** Shape expected by @udemy/react-globalization-components category modules. */
export interface CategoryUniqueLearnerStat {
    categoryId: number;
    uniqueLearnerCount: number;
}

export type CategoryUniqueLearnerStats = CategoryUniqueLearnerStat[];

/** Countries the popular categories module fetches stats for. Add new markets here. */
const CATEGORY_STATS_COUNTRIES = ['JP'] as const;

type CategoryStatsCountry = (typeof CATEGORY_STATS_COUNTRIES)[number];

export function isCategoryStatsCountry(countryCode: string): countryCode is CategoryStatsCountry {
    return (CATEGORY_STATS_COUNTRIES as readonly string[]).includes(countryCode);
}

/** Keyed by country code so callers can only look up a country we actually fetch. */
export type CategoryUniqueLearnerStatsMap = Partial<
    Record<CategoryStatsCountry, CategoryUniqueLearnerStats>
>;

/**
 * Reads the stats for a price country out of the map.
 * Returns null when we don't fetch stats for that country, or when its fetch failed —
 * the categories components then fall back to their hardcoded learner counts.
 */
export function getCategoryUniqueLearnerStatsForCountry(
    statsMap: CategoryUniqueLearnerStatsMap | undefined,
    countryCode: string,
): CategoryUniqueLearnerStats | null {
    return statsMap?.[countryCode as CategoryStatsCountry] ?? null;
}

async function fetchCategoryUniqueLearnerStatsForCountry(
    countryCode: CategoryStatsCountry,
): Promise<CategoryUniqueLearnerStats | null> {
    try {
        const data = await useCategoryUniqueLearnerStatsQuery.fetcher({countryCode})();
        const raw = data?.categoryUniqueLearnerStats;
        if (!raw?.length) return null;

        // GraphQL IDs are strings, so an unexpected value would coerce to NaN and reach
        // TopCategoriesComponent as an unusable category id. Drop those entries.
        const stats = raw
            .map((stat) => ({
                categoryId: Number(stat.courseCategory.id),
                uniqueLearnerCount: stat.uniqueLearnerCount,
            }))
            .filter((stat) => Number.isFinite(stat.categoryId));

        return stats.length ? stats : null;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch category unique learner stats:', error);
        return null;
    }
}

/**
 * Fetches stats for the visitor's price country when it is in {@link CATEGORY_STATS_COUNTRIES}.
 * Other countries skip GraphQL and return an empty map. A country is also omitted when its
 * request fails or returns no data. Returns an empty map when none succeed.
 */
export async function fetchCategoryUniqueLearnerStatsMap(
    priceCountryId: string,
): Promise<CategoryUniqueLearnerStatsMap> {
    if (!isCategoryStatsCountry(priceCountryId)) {
        return {};
    }

    const stats = await fetchCategoryUniqueLearnerStatsForCountry(priceCountryId);
    return stats ? {[priceCountryId]: stats} : {};
}
